import React, { useState, useEffect, useCallback } from "react";
import { Eye, DollarSign, Loader2, Activity } from "lucide-react";

const API_BASE = "/api";

const getUserDisplayName = (session, fallback = "Membre d'équipe") => {
  if (!session) return fallback;
  const names = [session.first_name, session.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (names) return names;
  if (session.session_name) return session.session_name;
  return fallback;
};

const SessionDetailsModal = ({ session, onClose, isDarkMode }) => {
  if (!session) return null;

  const modalOverlay = isDarkMode
    ? "bg-black/70 backdrop-blur-xl"
    : "bg-slate-900/50 backdrop-blur-xl";
  const cardClasses = isDarkMode
    ? "bg-[#0f172a] border border-white/10 text-slate-100"
    : "bg-white border border-slate-200 text-slate-900";
  const subtleText = isDarkMode ? "text-slate-400" : "text-slate-500";

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "—";
    return `${new Intl.NumberFormat("fr-FR").format(Number(value) || 0)} F`;
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const duration = (() => {
    if (!session.start_time) return "—";
    const start = new Date(session.start_time);
    const end = session.end_time ? new Date(session.end_time) : new Date();
    const diff = end - start;
    if (Number.isNaN(diff) || diff < 0) return "—";
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  })();

  const isCashier =
    (session.user_role || session.role || "")
      .toString()
      .toLowerCase()
      .includes("cash");

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${modalOverlay}`}>
      <div className={`${cardClasses} w-full max-w-3xl rounded-3xl p-6 shadow-2xl`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400">
              Session {session.session_index ?? "—"}
            </p>
            <h2 className="text-2xl font-semibold">
              {getUserDisplayName(session)}
            </h2>
            <p className={`text-sm ${subtleText}`}>{session.user_role || "Équipe"}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full border border-white/10 px-3 py-1 text-sm hover:border-amber-400"
          >
            Fermer
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4`}>
            <p className="text-xs uppercase tracking-wide text-amber-400 mb-2">
              Chronologie
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between">
                <span className={subtleText}>Début</span>
                <span>{formatDateTime(session.start_time)}</span>
              </li>
              <li className="flex justify-between">
                <span className={subtleText}>Fin</span>
                <span>{formatDateTime(session.end_time)}</span>
              </li>
              <li className="flex justify-between">
                <span className={subtleText}>Durée</span>
                <span>{duration}</span>
              </li>
            </ul>
          </div>

          <div className={`${isCashier ? (isDarkMode ? "bg-emerald-500/10" : "bg-emerald-50") : (isDarkMode ? "bg-white/5" : "bg-slate-50")} rounded-2xl p-4`}>
            <p className="text-xs uppercase tracking-wide text-amber-400 mb-2">
              Résumé financier
            </p>
            {isCashier ? (
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className={subtleText}>Fond d&apos;ouverture</span>
                  <span>{formatCurrency(session.opening_cash)}</span>
                </li>
                <li className="flex justify-between">
                  <span className={subtleText}>Ventes totales</span>
                  <span>{formatCurrency(session.total_sales)}</span>
                </li>
              </ul>
            ) : (
              <p className={`text-sm ${subtleText}`}>
                Les données détaillées (tables servies, clients, chiffre d&apos;affaire)
                seront récupérées automatiquement dès que les commandes seront
                synchronisées.
              </p>
            )}
          </div>
        </div>

        {session.notes && (
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-4 mt-4 text-sm`}>
            <p className="text-xs uppercase tracking-wide text-amber-400 mb-1">Notes</p>
            <p>{session.notes}</p>
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-dashed border-white/15 p-4 text-sm">
          <p className="text-xs uppercase tracking-wide text-amber-400 mb-1">
            Récapitulatif des commandes
          </p>
          <p className={subtleText}>
            Cette fenêtre affichera bientôt les tables ouvertes, le nombre de clients
            servis et le journal de caisse associé à cette session.
          </p>
        </div>
      </div>
    </div>
  );
};

const SessionCard = ({ session, index, isDarkMode, onViewDetails }) => {
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";
  const chipClasses = session.status === "open"
    ? "bg-emerald-500/15 text-emerald-300"
    : "bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300";

  const container =
    isDarkMode
      ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
      : "bg-white border border-slate-200 shadow-xl shadow-slate-200/60";

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "—";
    return `${new Intl.NumberFormat("fr-FR").format(Number(value) || 0)} F`;
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const duration = (() => {
    if (!session.start_time) return "—";
    const start = new Date(session.start_time);
    const end = session.end_time ? new Date(session.end_time) : new Date();
    const diff = end - start;
    if (Number.isNaN(diff) || diff < 0) return "—";
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
  })();

  const isCashier =
    (session.user_role || session.role || "")
      .toString()
      .toLowerCase()
      .includes("cash");

  return (
    <div className={`${container} rounded-3xl p-5 flex flex-col gap-4`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className={`text-xs uppercase tracking-[0.4em] ${textMuted}`}>
            Session {index + 1}
          </p>
          <h3 className="text-xl font-semibold">
            {getUserDisplayName(session, `Session ${index + 1}`)}
          </h3>
          <p className={`text-sm ${textMuted}`}>{session.user_role || "Équipe"}</p>
        </div>
        <button
          onClick={() => onViewDetails(session, index + 1)}
          className="rounded-full border border-white/10 p-2 text-amber-400 hover:text-amber-300 hover:border-amber-400/60 transition"
        >
          <Eye size={18} />
        </button>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${chipClasses}`}>
          {session.status === "open" ? "Session ouverte" : "Session fermée"}
        </span>
        <span className={`text-xs ${textMuted}`}>{duration}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-2xl border border-white/10 p-3">
          <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Début</p>
          <p className="font-medium">{formatDateTime(session.start_time)}</p>
        </div>
        <div className="rounded-2xl border border-white/10 p-3">
          <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Fin</p>
          <p className="font-medium">{formatDateTime(session.end_time)}</p>
        </div>
      </div>

      {isCashier && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-3`}>
            <div className="text-xs uppercase tracking-wider flex items-center gap-1">
              <DollarSign size={14} />
              Fond d&apos;ouverture
            </div>
            <p className="font-semibold">{formatCurrency(session.opening_cash)}</p>
          </div>
          <div className={`${isDarkMode ? "bg-white/5" : "bg-slate-50"} rounded-2xl p-3`}>
            <div className="text-xs uppercase tracking-wider flex items-center gap-1">
              <Activity size={14} />
              Ventes totales
            </div>
            <p className="font-semibold">{formatCurrency(session.total_sales)}</p>
          </div>
        </div>
      )}

      {session.notes && (
        <p className={`text-sm italic ${textMuted}`}>{session.notes}</p>
      )}
    </div>
  );
};

const SessionManagement = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [detailsSession, setDetailsSession] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  useEffect(() => {
    const target = document.documentElement;
    const syncTheme = () => setIsDarkMode(target.classList.contains("dark"));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const containerBg = isDarkMode
    ? "bg-gradient-to-br from-[#050b18] via-[#0f1c33] to-[#050b18] text-slate-100"
    : "bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900";
  const cardClasses = isDarkMode
    ? "bg-white/5 border border-white/10 shadow-2xl shadow-black/40"
    : "bg-white border border-slate-100 shadow-xl shadow-slate-200/60";
  const textMuted = isDarkMode ? "text-slate-400" : "text-slate-500";

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/sessions`);
      if (!response.ok) throw new Error("Erreur réseau ou serveur");
      const data = await response.json();
      setSessions(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const filteredSessions =
    filterStatus === "all"
      ? sessions
      : sessions.filter((session) => session.status === filterStatus);

  const stats = {
    total: sessions.length,
    open: sessions.filter((s) => s.status === "open").length,
    closed: sessions.filter((s) => s.status === "closed").length,
  };

  const statCards = [
    { label: "Total sessions", value: stats.total, accent: "from-amber-400 to-orange-500" },
    { label: "Sessions ouvertes", value: stats.open, accent: "from-emerald-400 to-teal-400" },
    { label: "Sessions fermées", value: stats.closed, accent: "from-slate-400 to-slate-600" },
  ];

  const handleViewDetails = (session, position) => {
    setDetailsSession({ ...session, session_index: position });
  };

  return (
    <div className={`min-h-screen px-4 py-6 ${containerBg}`}>
      {detailsSession && (
        <SessionDetailsModal
          session={detailsSession}
          onClose={() => setDetailsSession(null)}
          isDarkMode={isDarkMode}
        />
      )}

      <div className="max-w-7xl w-full mx-auto space-y-6">
        <header className={`${cardClasses} rounded-3xl p-6 flex flex-col gap-3`}>
          <div>
            <p className={`text-xs uppercase tracking-[0.4em] ${textMuted}`}>
              Sessions d&apos;équipe
            </p>
            <h1 className="text-3xl font-bold tracking-tight">Journal des connexions</h1>
            <p className={`text-sm ${textMuted}`}>
              Suivez les équipes actuellement en session et celles qui ont clôturé leur
              service aujourd&apos;hui.
            </p>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="rounded-3xl bg-gradient-to-br from-white/10 to-white/5 border border-white/10 p-5 shadow-inner shadow-black/10"
            >
              <p className={`text-xs uppercase tracking-[0.3em] ${textMuted}`}>
                {card.label}
              </p>
              <p className="text-3xl font-semibold mt-2">{card.value}</p>
            </div>
          ))}
        </div>

        <div className={`${cardClasses} rounded-3xl p-5`}>
          <p className={`text-xs uppercase tracking-[0.4em] ${textMuted} mb-4`}>
            Filtres rapides
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { value: "all", label: "Toutes les sessions" },
              { value: "open", label: "Sessions ouvertes" },
              { value: "closed", label: "Sessions fermées" },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setFilterStatus(filter.value)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  filterStatus === filter.value
                    ? "bg-amber-400 text-slate-900"
                    : "bg-white/10 text-inherit hover:bg-white/20"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`${cardClasses} rounded-3xl`}>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-3">
              <Loader2 className="animate-spin" size={20} />
              Chargement des sessions en cours...
            </div>
          ) : error ? (
            <p className="p-6 text-red-400">{error}</p>
          ) : filteredSessions.length === 0 ? (
            <div className="p-6 text-center text-sm">
              Aucune session ne correspond à ce filtre pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4">
              {filteredSessions.map((session, index) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  index={index}
                  isDarkMode={isDarkMode}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;
